import { useState, useEffect } from 'react'
import { supabase } from '../services/supabase'
import { offlineDB } from '../services/offlineDB'
import { Calendar, X, Printer } from 'lucide-react'
import { imprimirResumenSemanal } from '../utils/printer'

export default function TotalesSemanaAnterior({ onClose }) {
  const [fechaInicio, setFechaInicio] = useState('')
  const [fechaFin, setFechaFin] = useState('')
  const [datos, setDatos] = useState(null)
  const [loading, setLoading] = useState(false)
  const [configuracion, setConfiguracion] = useState(null)
  const [estadisticas, setEstadisticas] = useState(null)

  useEffect(() => {
    cargarConfiguracion()
    const fechas = calcularSemanaAnterior()
    setFechaInicio(fechas.inicio)
    setFechaFin(fechas.fin)
    cargarDatos(fechas.inicio, fechas.fin)
  }, [])

  async function cargarConfiguracion() {
    const { data } = await supabase
      .from('configuracion')
      .select('*')
      .limit(1)
      .single()
    
    setConfiguracion(data || { nombre_empresa: 'Panadería Hermanos Alonso S.L.' })
  }

  // ⭐ NUEVA: Calcular datos de la semana anterior para comparación
  async function calcularComparacionSemanaAnterior(datosActuales, fechaInicio, fechaFin) {
    try {
      // Calcular fechas de la semana anterior
      const inicioActual = new Date(fechaInicio + 'T00:00:00')
      const inicioAnterior = new Date(inicioActual)
      inicioAnterior.setDate(inicioActual.getDate() - 7)
      
      const finAnterior = new Date(fechaFin + 'T00:00:00')
      finAnterior.setDate(finAnterior.getDate() - 7)
      
      const fechaInicioAnterior = inicioAnterior.toISOString().split('T')[0]
      const fechaFinAnterior = finAnterior.toISOString().split('T')[0]
      
      // Cargar datos de semana anterior
      const { data: deliveries } = await supabase
        .from('deliveries')
        .select(`
          *,
          cliente:clients(id, descuento_porcentaje),
          lineas:delivery_lines(
            product_id,
            cantidad_entregada,
            cantidad_devuelta,
            precio_unit,
            descuento_aplicable
          )
        `)
        .gte('fecha', fechaInicioAnterior)
        .lte('fecha', fechaFinAnterior)

      if (!deliveries || deliveries.length === 0) {
        return null
      }

      // Precios especiales actuales
      const clientIds = [...new Set(deliveries.map(d => d.client_id))]
      const { data: cpData } = await supabase
        .from('client_prices')
        .select('client_id, product_id, pvp')
        .in('client_id', clientIds)
        .eq('activo', true)
      const cpMap = {}
      ;(cpData || []).forEach(cp => {
        if (!cpMap[cp.client_id]) cpMap[cp.client_id] = {}
        cpMap[cp.client_id][cp.product_id] = Number(cp.pvp)
      })

      // Calcular totales semana anterior con misma lógica que AlbaranDialog
      const clientesMap = {}

      deliveries.forEach(delivery => {
        const clienteId = delivery.client_id
        const cliente = delivery.cliente

        if (!clientesMap[clienteId]) {
          clientesMap[clienteId] = { id: clienteId, subConDesc: 0, subSinDesc: 0, margen: Number(cliente?.descuento_porcentaje || 0) }
        }

        delivery.lineas?.forEach(linea => {
          const cantidadNeta = (linea.cantidad_entregada || 0) - (linea.cantidad_devuelta || 0)
          if (cantidadNeta <= 0) return
          const precioUnit = cpMap[clienteId]?.[linea.product_id] ?? Number(linea.precio_unit || 0)
          const subtotal = cantidadNeta * precioUnit
          if (linea.descuento_aplicable) clientesMap[clienteId].subConDesc += subtotal
          else clientesMap[clienteId].subSinDesc += subtotal
        })
      })

      Object.values(clientesMap).forEach(c => {
        c.total = (c.subConDesc * (1 - c.margen / 100)) + c.subSinDesc
      })

      const datosAnterior = Object.values(clientesMap).filter(c => c.total > 0)
      const totalAnterior = datosAnterior.reduce((sum, c) => sum + c.total, 0)
      
      // Comparar
      const totalActual = datosActuales.reduce((sum, c) => sum + c.total, 0)
      const diferenciaPorcentaje = totalAnterior > 0 
        ? ((totalActual - totalAnterior) / totalAnterior * 100)
        : 0
      
      // Detectar clientes con cambios significativos
      const cambiosSignificativos = []
      datosActuales.forEach(clienteActual => {
        const clienteAnterior = datosAnterior.find(c => c.id === clienteActual.id)
        if (clienteAnterior) {
          const cambio = ((clienteActual.total - clienteAnterior.total) / clienteAnterior.total * 100)
          if (Math.abs(cambio) > 30) { // Cambio mayor al 30%
            cambiosSignificativos.push({
              nombre: clienteActual.nombre,
              cambio: cambio,
              anterior: clienteAnterior.total,
              actual: clienteActual.total
            })
          }
        }
      })
      
      // Detectar clientes nuevos (en actual pero no en anterior)
      const clientesNuevos = datosActuales.filter(ca => 
        !datosAnterior.find(ant => ant.id === ca.id)
      )
      
      return {
        totalAnterior,
        diferenciaPorcentaje,
        cambiosSignificativos,
        clientesNuevos
      }
      
    } catch (error) {
      console.error('Error calculando comparación:', error)
      return null
    }
  }

  function calcularSemanaAnterior() {
    // localISO evita el bug de zona horaria: medianoche local = día anterior en UTC
    function localISO(d) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    }

    const hoy = new Date()
    const diaSemana = hoy.getDay()
    // Semana española: lunes (1) a domingo (0)
    const diasHastaLunesActual = diaSemana === 0 ? 6 : diaSemana - 1

    const lunesActual = new Date(hoy)
    lunesActual.setDate(hoy.getDate() - diasHastaLunesActual)

    const lunesAnterior = new Date(lunesActual)
    lunesAnterior.setDate(lunesActual.getDate() - 7)

    const domingoAnterior = new Date(lunesAnterior)
    domingoAnterior.setDate(lunesAnterior.getDate() + 6)

    return {
      inicio: localISO(lunesAnterior),
      fin: localISO(domingoAnterior)
    }
  }

  async function cargarDatos(inicio, fin) {
    try {
      setLoading(true)
      setDatos(null)

      console.log('🔍 Cargando datos desde:', inicio, 'hasta:', fin)

      // PASO 1: Cargar deliveries — online: Supabase; offline: caché local
      let deliveries = null
      let usandoCache = false

      if (navigator.onLine) {
        try {
          const { data, error } = await supabase
            .from('deliveries')
            .select(`
              *,
              cliente:clients(
                id,
                alias,
                razon_social,
                descuento_porcentaje,
                recargo_equivalencia
              ),
              lineas:delivery_lines(
                product_id,
                cantidad_entregada,
                cantidad_devuelta,
                precio_unit,
                descuento_aplicable,
                producto:products(nombre)
              )
            `)
            .gte('fecha', inicio)
            .lte('fecha', fin)
            .order('fecha', { ascending: true })

          if (error) throw error
          deliveries = data
        } catch (err) {
          console.warn('⚠️ Error online, intentando caché:', err.message)
        }
      }

      // Fallback: usar historial cacheado en IndexedDB
      if (!deliveries || deliveries.length === 0) {
        const cached = await offlineDB.getDeliveriesHistory(inicio, fin)
        if (cached && cached.length > 0) {
          usandoCache = true
          console.log(`📦 Usando ${cached.length} albaranes del caché local`)
          // Normalizar estructura: delivery_lines → lineas, client lookup → cliente
          const clientesLocal = await offlineDB.getCachedClients()
          const clientesById = {}
          clientesLocal.forEach(c => { clientesById[c.id] = c })

          deliveries = cached.map(d => ({
            ...d,
            cliente: clientesById[d.client_id] || { id: d.client_id, alias: null, razon_social: 'Desconocido', descuento_porcentaje: 0 },
            lineas: (d.delivery_lines || [])
          }))
        }
      }

      console.log(`✅ ${deliveries?.length || 0} deliveries encontrados${usandoCache ? ' (caché)' : ''}`)

      if (!deliveries || deliveries.length === 0) {
        if (!navigator.onLine) {
          alert('Sin conexión y sin datos en caché para este período.\nConéctate a internet una vez para que la tablet guarde el historial de entregas.')
        }
        setDatos({
          clientes: [],
          totales: { unidades: 0, importe: 0, clientesActivos: 0 }
        })
        return
      }

      // PASO 2: Cargar precios especiales — solo online (en caché se usa precio_unit del albarán)
      const clientIds = [...new Set(deliveries.map(d => d.client_id))]
      let cpData = null
      if (navigator.onLine) {
        const { data } = await supabase
          .from('client_prices')
          .select('client_id, product_id, pvp')
          .in('client_id', clientIds)
          .eq('activo', true)
        cpData = data
      }

      const clientPricesMap = {}
      ;(cpData || []).forEach(cp => {
        if (!clientPricesMap[cp.client_id]) clientPricesMap[cp.client_id] = {}
        clientPricesMap[cp.client_id][cp.product_id] = Number(cp.pvp)
      })

      // PASO 3: Agrupar por cliente — misma lógica que AlbaranDialog.cargarResumen()
      const clientesMap = {}

      deliveries.forEach(delivery => {
        const clienteId = delivery.client_id
        const cliente = delivery.cliente
        const nombreCliente = cliente?.alias || cliente?.razon_social || 'Cliente desconocido'

        if (!clientesMap[clienteId]) {
          clientesMap[clienteId] = {
            id: clienteId,
            nombre: nombreCliente,
            subtotalConDescuento: 0,
            subtotalSinDescuento: 0,
            unidades: 0,
            margen: Number(cliente?.descuento_porcentaje || 0)
          }
        }

        delivery.lineas?.forEach(linea => {
          // Cantidad neta: devoluciones no se cobran
          const cantidadNeta = (linea.cantidad_entregada || 0) - (linea.cantidad_devuelta || 0)
          if (cantidadNeta <= 0) return

          // Precio especial actual del cliente, o el almacenado en el albarán
          const precioUnit = clientPricesMap[clienteId]?.[linea.product_id]
            ?? Number(linea.precio_unit || 0)

          const subtotal = cantidadNeta * precioUnit

          if (linea.descuento_aplicable) {
            clientesMap[clienteId].subtotalConDescuento += subtotal
          } else {
            clientesMap[clienteId].subtotalSinDescuento += subtotal
          }
          clientesMap[clienteId].unidades += cantidadNeta
        })
      })

      // Calcular total: descuento solo sobre productos que lo llevan
      Object.values(clientesMap).forEach(c => {
        const descPct = c.margen / 100
        c.total = (c.subtotalConDescuento * (1 - descPct)) + c.subtotalSinDescuento
      })

      const datosAgrupados = Object.values(clientesMap)
        .filter(c => c.total > 0)
        .sort((a, b) => a.nombre.localeCompare(b.nombre))

      const totales = {
        unidades: datosAgrupados.reduce((sum, c) => sum + c.unidades, 0),
        importe: datosAgrupados.reduce((sum, c) => sum + c.total, 0),
        clientesActivos: datosAgrupados.length
      }

      console.log('📊 Totales calculados:', totales)
      console.log('💰 Ejemplo primer cliente:', datosAgrupados[0])
      setDatos({ clientes: datosAgrupados, totales })
      
      // Calcular estadísticas comparativas automáticamente
      const stats = await calcularComparacionSemanaAnterior(datosAgrupados, inicio, fin)
      setEstadisticas(stats)
      
    } catch (err) {
      console.error('❌ Error completo:', err)
      alert('Error al cargar datos: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  function handleFechaChange(tipo, valor) {
    if (tipo === 'inicio') {
      setFechaInicio(valor)
      cargarDatos(valor, fechaFin)
    } else {
      setFechaFin(valor)
      cargarDatos(fechaInicio, valor)
    }
  }

  // ⭐ SIMPLIFICADA: Solo pregunta formato
  async function imprimirTicket() {
    if (!datos || !configuracion) {
      alert('No hay datos para imprimir')
      return
    }

    const opcion = confirm(
      '¿En qué formato desea imprimir?\n\n' +
      'OK = 80mm (ancho)\n' +
      'Cancelar = 58mm (estrecho)'
    )
    
    const formato = opcion ? '80mm' : '58mm'

    try {
      setLoading(true)
      console.log(`🖨️ Imprimiendo resumen semanal en ${formato}...`)
      
      await imprimirResumenSemanal(
        datos.clientes,
        datos.totales,
        fechaInicio,
        fechaFin,
        configuracion,
        formato
      )
      
      console.log('✅ Resumen impreso correctamente')
    } catch (error) {
      console.error('❌ Error al imprimir:', error)
      alert('Error al imprimir: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  function formatearFecha(fechaISO) {
    if (!fechaISO) return ''
    const fecha = new Date(fechaISO + 'T00:00:00')
    return fecha.toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    })
  }

  function formatearMoneda(cantidad) {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(cantidad)
  }

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}
      onClick={onClose}
    >
      <div 
        style={{
          background: 'var(--bg)',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '800px',
          maxHeight: '90vh',
          overflow: 'auto'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1.5rem',
          borderBottom: '1px solid var(--border)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Calendar size={24} />
            <h2 style={{ margin: 0 }}>Totales Semana Anterior</h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.5rem' }}>
          {/* Selector de fechas */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
            marginBottom: '1.5rem',
            padding: '1rem',
            background: 'var(--bg-secondary)',
            borderRadius: '8px'
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem',
                fontSize: '0.875rem',
                color: 'var(--muted)'
              }}>
                Del (Lunes)
              </label>
              <input
                type="date"
                value={fechaInicio}
                onChange={e => handleFechaChange('inicio', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '6px',
                  border: '1px solid var(--border)',
                  background: 'var(--bg)',
                  color: 'var(--fg)'
                }}
              />
            </div>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem',
                fontSize: '0.875rem',
                color: 'var(--muted)'
              }}>
                Al (Domingo)
              </label>
              <input
                type="date"
                value={fechaFin}
                onChange={e => handleFechaChange('fin', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '6px',
                  border: '1px solid var(--border)',
                  background: 'var(--bg)',
                  color: 'var(--fg)'
                }}
              />
            </div>
          </div>

          {/* Panel de estadísticas comparativas */}
          {estadisticas && (
            <div style={{
              padding: '1rem',
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(147, 51, 234, 0.1))',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '8px',
              marginBottom: '1.5rem'
            }}>
              <div style={{ 
                fontSize: '0.875rem', 
                fontWeight: 'bold',
                marginBottom: '0.75rem',
                color: 'var(--accent)'
              }}>
                📊 Comparación con semana anterior
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '0.75rem'
              }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>Semana anterior</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                    {formatearMoneda(estadisticas.totalAnterior)}
                  </div>
                </div>
                
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>Variación</div>
                  <div style={{ 
                    fontSize: '1.25rem', 
                    fontWeight: 'bold',
                    color: estadisticas.diferenciaPorcentaje > 0 ? '#10b981' : '#ef4444'
                  }}>
                    {estadisticas.diferenciaPorcentaje > 0 ? '📈 ↗' : '📉 ↘'} 
                    {' '}
                    {estadisticas.diferenciaPorcentaje > 0 ? '+' : ''}
                    {estadisticas.diferenciaPorcentaje.toFixed(1)}%
                  </div>
                </div>
              </div>

              {estadisticas.clientesNuevos.length > 0 && (
                <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid rgba(59, 130, 246, 0.2)' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>
                    ✨ Clientes nuevos ({estadisticas.clientesNuevos.length})
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    {estadisticas.clientesNuevos.slice(0, 3).map(c => (
                      <div key={c.nombre} style={{ fontSize: '0.875rem' }}>
                        • {c.nombre}: {formatearMoneda(c.total)}
                      </div>
                    ))}
                    {estadisticas.clientesNuevos.length > 3 && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--muted)', fontStyle: 'italic' }}>
                        ... y {estadisticas.clientesNuevos.length - 3} más
                      </div>
                    )}
                  </div>
                </div>
              )}

              {estadisticas.cambiosSignificativos.length > 0 && (
                <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid rgba(59, 130, 246, 0.2)' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>
                    ⚠️ Cambios significativos (±30%)
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    {estadisticas.cambiosSignificativos.slice(0, 3).map(c => (
                      <div key={c.nombre} style={{ fontSize: '0.875rem' }}>
                        {c.cambio > 0 ? '↗' : '↘'} {c.nombre}: {c.cambio > 0 ? '+' : ''}{c.cambio.toFixed(0)}%
                        <span style={{ fontSize: '0.75rem', color: 'var(--muted)', marginLeft: '0.5rem' }}>
                          ({formatearMoneda(c.anterior)} → {formatearMoneda(c.actual)})
                        </span>
                      </div>
                    ))}
                    {estadisticas.cambiosSignificativos.length > 3 && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--muted)', fontStyle: 'italic' }}>
                        ... y {estadisticas.cambiosSignificativos.length - 3} más
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <div style={{ marginBottom: '1rem' }}>⏳</div>
              Cargando datos...
            </div>
          ) : datos ? (
            <>
              {/* Resumen compacto */}
              <div style={{
                padding: '1rem',
                background: 'var(--accent-bg)',
                borderRadius: '8px',
                marginBottom: '1.5rem',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>
                  Total a cobrar esta semana
                </div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--accent)' }}>
                  {formatearMoneda(datos.totales.importe)}
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--muted)', marginTop: '0.5rem' }}>
                  {datos.totales.clientesActivos} clientes activos
                </div>
              </div>

              {/* Lista simple de clientes */}
              {datos.clientes.length > 0 ? (
                <div style={{
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--bg-secondary)' }}>
                        <th style={{ 
                          padding: '0.75rem', 
                          textAlign: 'left',
                          borderBottom: '1px solid var(--border)'
                        }}>
                          Cliente
                        </th>
                        <th style={{ 
                          padding: '0.75rem', 
                          textAlign: 'right',
                          borderBottom: '1px solid var(--border)',
                          width: '150px'
                        }}>
                          A cobrar
                        </th>
                        <th style={{ 
                          padding: '0.75rem', 
                          textAlign: 'center',
                          borderBottom: '1px solid var(--border)',
                          width: '60px'
                        }}>
                          ✓
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {datos.clientes.map((cliente, index) => (
                        <tr key={cliente.id}>
                          <td style={{ 
                            padding: '0.75rem',
                            borderBottom: index < datos.clientes.length - 1 ? '1px solid var(--border)' : 'none'
                          }}>
                            {cliente.nombre}
                          </td>
                          <td style={{ 
                            padding: '0.75rem', 
                            textAlign: 'right',
                            fontWeight: 'bold',
                            fontSize: '1.1rem',
                            borderBottom: index < datos.clientes.length - 1 ? '1px solid var(--border)' : 'none'
                          }}>
                            {formatearMoneda(cliente.total)}
                          </td>
                          <td style={{ 
                            padding: '0.75rem', 
                            textAlign: 'center',
                            borderBottom: index < datos.clientes.length - 1 ? '1px solid var(--border)' : 'none',
                            color: 'var(--muted)'
                          }}>
                            ☐
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '3rem',
                  color: 'var(--muted)'
                }}>
                  No hay datos para el período seleccionado
                </div>
              )}
            </>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '3rem',
              color: 'var(--muted)'
            }}>
              No hay datos para mostrar
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          justifyContent: 'flex-end',
          padding: '1.5rem',
          borderTop: '1px solid var(--border)'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'var(--bg-secondary)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              color: 'var(--fg)'
            }}
          >
            Cerrar
          </button>
          <button
            onClick={imprimirTicket}
            disabled={loading || !datos || datos.clientes.length === 0}
            style={{
              padding: '0.75rem 1.5rem',
              background: datos && datos.clientes.length > 0 ? 'var(--accent)' : 'var(--muted)',
              border: 'none',
              borderRadius: '6px',
              cursor: datos && datos.clientes.length > 0 ? 'pointer' : 'not-allowed',
              color: '#000',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontWeight: 'bold',
              opacity: loading ? 0.5 : 1
            }}
          >
            <Printer size={18} />
            {loading ? 'Imprimiendo...' : 'Imprimir (58mm/80mm)'}
          </button>
        </div>
      </div>
    </div>
  )
}
